/** Column-major 4×4 rigid math and `XRRigidTransform`, right-handed like WebXR. */

import type { Mat4 } from './protocol';

/** A column-major 4×4: a protocol array or a `Float32Array`. */
type Matrix = ArrayLike<number>;

export interface XYZW { x: number; y: number; z: number; w: number }

export const IDENTITY: Mat4 = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

export function multiply(a: Matrix, b: Matrix): number[] {
    const out = new Array<number>(16);
    for (let c = 0; c < 4; c++) {
        for (let r = 0; r < 4; r++) {
            out[c * 4 + r] = a[r] * b[c * 4] + a[4 + r] * b[c * 4 + 1] + a[8 + r] * b[c * 4 + 2] + a[12 + r] * b[c * 4 + 3];
        }
    }
    return out;
}

/** Inverse of a rotation + translation: Rᵀ, −Rᵀt. */
export function invertRigid(m: Matrix): number[] {
    const [tx, ty, tz] = [m[12], m[13], m[14]];
    return [
        m[0], m[4], m[8], 0,
        m[1], m[5], m[9], 0,
        m[2], m[6], m[10], 0,
        -(m[0] * tx + m[1] * ty + m[2] * tz),
        -(m[4] * tx + m[5] * ty + m[6] * tz),
        -(m[8] * tx + m[9] * ty + m[10] * tz),
        1,
    ];
}

export function fromPose(p: XYZW, q: XYZW): number[] {
    const { x, y, z, w } = q;
    return [
        1 - 2 * (y * y + z * z), 2 * (x * y + z * w), 2 * (x * z - y * w), 0,
        2 * (x * y - z * w), 1 - 2 * (x * x + z * z), 2 * (y * z + x * w), 0,
        2 * (x * z + y * w), 2 * (y * z - x * w), 1 - 2 * (x * x + y * y), 0,
        p.x, p.y, p.z, 1,
    ];
}

function quatFromMatrix(m: Matrix): XYZW {
    const [m00, m11, m22] = [m[0], m[5], m[10]];
    const trace = m00 + m11 + m22;
    let q: XYZW;
    if (trace > 0) {
        const s = 0.5 / Math.sqrt(trace + 1);
        q = { w: 0.25 / s, x: (m[6] - m[9]) * s, y: (m[8] - m[2]) * s, z: (m[1] - m[4]) * s };
    } else if (m00 > m11 && m00 > m22) {
        const s = 2 * Math.sqrt(1 + m00 - m11 - m22);
        q = { w: (m[6] - m[9]) / s, x: 0.25 * s, y: (m[4] + m[1]) / s, z: (m[8] + m[2]) / s };
    } else if (m11 > m22) {
        const s = 2 * Math.sqrt(1 + m11 - m00 - m22);
        q = { w: (m[8] - m[2]) / s, x: (m[4] + m[1]) / s, y: 0.25 * s, z: (m[9] + m[6]) / s };
    } else {
        const s = 2 * Math.sqrt(1 + m22 - m00 - m11);
        q = { w: (m[1] - m[4]) / s, x: (m[8] + m[2]) / s, y: (m[9] + m[6]) / s, z: 0.25 * s };
    }
    const n = Math.hypot(q.x, q.y, q.z, q.w);
    return { x: q.x / n, y: q.y / n, z: q.z / n, w: q.w / n };
}

/** `XRRigidTransform`: Babylon constructs it, and reads position, orientation and matrix. */
export class RigidTransform {
    readonly position: Readonly<XYZW>;
    readonly orientation: Readonly<XYZW>;
    readonly matrix: Float32Array;
    private _inverse: RigidTransform | null = null;

    constructor(position: Partial<XYZW> = {}, orientation: Partial<XYZW> = {}) {
        this.position = Object.freeze({ x: position.x ?? 0, y: position.y ?? 0, z: position.z ?? 0, w: 1 });
        const q = { x: orientation.x ?? 0, y: orientation.y ?? 0, z: orientation.z ?? 0, w: orientation.w ?? 1 };
        const n = Math.hypot(q.x, q.y, q.z, q.w);
        if (!(n > 0)) throw new DOMException('Orientation must not be zero-length.', 'InvalidStateError');
        this.orientation = Object.freeze({ x: q.x / n, y: q.y / n, z: q.z / n, w: q.w / n });
        this.matrix = Float32Array.from(fromPose(this.position, this.orientation));
    }

    static fromMatrix(m: Matrix): RigidTransform {
        return new RigidTransform({ x: m[12], y: m[13], z: m[14] }, quatFromMatrix(m));
    }

    get inverse(): RigidTransform {
        this._inverse ??= RigidTransform.fromMatrix(invertRigid(this.matrix));
        return this._inverse;
    }
}

/**
 * The projection with its depth terms rebuilt from the render state, so
 * `depthNear`/`depthFar` changes need no round trip to native.
 */
export function withDepthRange(projection: Matrix, near: number, far: number): Float32Array {
    const out = Float32Array.from(projection);
    out[10] = -(far + near) / (far - near);
    out[14] = -2 * far * near / (far - near);
    return out;
}

/**
 * World-from-ray for a screen point at normalized device coordinates
 * (x right, y up, both −1…1): the view's origin, turned so its −Z passes
 * through the point.
 */
export function screenRay(worldFromView: Matrix, projection: Matrix, ndcX: number, ndcY: number): number[] {
    // At view-space z = −1, ndcX = m0·x − m8, so x = (ndcX + m8)/m0; y likewise.
    const d = [(ndcX + projection[8]) / projection[0], (ndcY + projection[9]) / projection[5], -1];
    const len = Math.hypot(d[0], d[1], d[2]);
    const [dx, dy, dz] = [d[0] / len, d[1] / len, d[2] / len];
    // Shortest rotation taking (0, 0, −1) to d: axis (0,0,−1) × d, half-angle form.
    const w = 1 - dz;
    const q = { x: dy, y: -dx, z: 0, w };
    const n = Math.hypot(q.x, q.y, q.w);
    return multiply(worldFromView, fromPose({ x: 0, y: 0, z: 0, w: 1 }, { x: q.x / n, y: q.y / n, z: 0, w: q.w / n }));
}
